import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core'
import { Subscription } from 'rxjs'
import { finalize } from 'rxjs/operators'
import { ApiService } from 'src/app/services/api.service'
import { AccessTemplatesDto, ApplicationType, SyncPairAccessDto, SyncPairInfo } from 'src/app/services/dtos'
import { UiService } from 'src/app/services/ui.service'
import { handleArpioError } from 'src/utils/handle-error'
import { subscriptionActive } from 'src/utils/rxjs-utils'
import { AccountService } from 'src/app/account/services/account.service'
import { AccessScope, AccessService } from 'src/app/services/access.service'
import { isTopmostModal, popModal, pushModal } from 'src/app/shared/modal-dialog/modal-key-stack'

@Component({
  selector: 'update-access-dialog',
  templateUrl: './update-access-dialog.component.html',
  styleUrls: ['./update-access-dialog.component.scss'],
  standalone: false,
})
export class UpdateAccessDialogComponent implements OnInit, OnDestroy {
  @Input() applicationType: ApplicationType = ApplicationType.AWS
  @Input() sourceEndpointUri: string = null
  @Input() targetEndpointUri: string = null
  @Input() identityProviderId: string = null
  @Input() accessCheckResults: SyncPairAccessDto = null
  @Output() done = new EventEmitter<null>()
  @Output() cancel = new EventEmitter<null>()

  // Esc = Cancel. Listens at the document level so the key fires no matter
  // where focus sits inside the modal. Topmost-modal gate prevents stacked
  // dialogs from both dismissing on a single Esc press.
  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKeydown(event: KeyboardEvent): void {
    if (!isTopmostModal(this)) return
    event.preventDefault()
    this.onCancel()
  }

  sourceAccessService: AccessService = null
  targetAccessService: AccessService = null
  accessTemplates: AccessTemplatesDto = null
  AwsAccessScope = AccessScope

  private accessStatusSubscription: Subscription = null
  private accessTemplatesSubscription: Subscription = null

  constructor(
    private accountService: AccountService,
    private apiService: ApiService,
    public uiService: UiService
  ) {}

  ngOnInit() {
    pushModal(this)
    if (this.accessCheckResults) {
      this.sourceAccessService = new AccessService(
        this.apiService,
        this.uiService,
        this as SyncPairInfo,
        AccessScope.SOURCE,
        this.accessCheckResults
      )
      this.targetAccessService = new AccessService(
        this.apiService,
        this.uiService,
        this as SyncPairInfo,
        AccessScope.TARGET,
        this.accessCheckResults
      )
    } else {
      this.accessStatusSubscription = this.apiService
        .testSyncPairAccess(this as SyncPairInfo)
        .pipe(finalize(() => (this.accessStatusSubscription = null)))
        .subscribe(
          (accessDto) => {
            this.accessCheckResults = accessDto
            this.sourceAccessService = new AccessService(
              this.apiService,
              this.uiService,
              this as SyncPairInfo,
              AccessScope.SOURCE,
              accessDto
            )
            this.targetAccessService = new AccessService(
              this.apiService,
              this.uiService,
              this as SyncPairInfo,
              AccessScope.TARGET,
              accessDto
            )
          },
          handleArpioError(window.location.href, this.apiService, this.uiService)
        )
    }

    this.accessTemplatesSubscription = this.apiService
      .getAccessTemplates(
        this.accountService.accountId,
        this.sourceEndpointUri,
        this.targetEndpointUri,
        this.identityProviderId
      )
      .pipe(finalize(() => (this.accessTemplatesSubscription = null)))
      .subscribe(
        (accessTemplatesDto) => (this.accessTemplates = accessTemplatesDto),
        handleArpioError(window.location.href, this.apiService, this.uiService)
      )
  }

  ngOnDestroy() {
    popModal(this)
    if (subscriptionActive(this.accessStatusSubscription)) {
      this.accessStatusSubscription.unsubscribe()
    }
    this.accessStatusSubscription = null

    if (subscriptionActive(this.accessTemplatesSubscription)) {
      this.accessTemplatesSubscription.unsubscribe()
    }
    this.accessTemplatesSubscription = null
  }

  onCancel() {
    this.cancel.emit(null)
  }

  onFinish() {
    this.done.emit(null)
  }

  get loadingAccessTemplates() {
    return subscriptionActive(this.accessTemplatesSubscription) || subscriptionActive(this.accessStatusSubscription)
  }

  get accountId() {
    return this.accountService.accountId
  }

  get sourceAccessTemplate() {
    return this.accessTemplates.sourceTemplateS3Url
  }

  get sourceInstallCommand() {
    return this.accessTemplates.sourceInstallCommand
  }

  get sourceAccountId() {
    return this.sourceEndpointUri.split(':')[1]
  }

  get sourceRegion() {
    return this.sourceEndpointUri.split(':')[2]
  }

  get targetAccessTemplate() {
    return this.accessTemplates.targetTemplateS3Url
  }

  get targetInstallCommand() {
    return this.accessTemplates.targetInstallCommand
  }

  get targetAccountId() {
    return this.targetEndpointUri.split(':')[1]
  }

  get targetRegion() {
    return this.targetEndpointUri.split(':')[2]
  }

  get accessChecksComplete() {
    return this.sourceAccessService.allGood && this.targetAccessService.allGood
  }

  get stackSetsEnabled(): boolean {
    return !!(
      this.accessTemplates?.stackSetsTemplate?.sourceStackSetTemplateS3Url ||
      this.accessTemplates?.stackSetsTemplate?.targetStackSetTemplateS3Url ||
      this.accessTemplates?.stackSetsTemplate?.failbackStackSetTemplateS3Url
    )
  }

  get sourceStackSetTemplateUrl(): string | undefined {
    return this.accessTemplates?.stackSetsTemplate?.sourceStackSetTemplateS3Url
  }

  get targetStackSetTemplateUrl(): string | undefined {
    return this.accessTemplates?.stackSetsTemplate?.targetStackSetTemplateS3Url
  }

  get failbackStackSetTemplateUrl(): string | undefined {
    return this.accessTemplates?.stackSetsTemplate?.failbackStackSetTemplateS3Url
  }

  get sourceTemplateBucket(): string | undefined {
    return this.accessTemplates?.stackSetsTemplate?.sourceTemplate?.bucket
  }

  get sourceTemplateKey(): string | undefined {
    return this.accessTemplates?.stackSetsTemplate?.sourceTemplate?.key
  }

  get targetTemplateBucket(): string | undefined {
    return this.accessTemplates?.stackSetsTemplate?.targetTemplate?.bucket
  }

  get targetTemplateKey(): string | undefined {
    return this.accessTemplates?.stackSetsTemplate?.targetTemplate?.key
  }

  get regenerationJobId(): string | null {
    return this.accessTemplates?.stackSetsTemplate?.jobId ?? null
  }

  protected readonly Scope = AccessScope
}
